import React, { useState, useEffect, useCallback } from 'react';
import _ from 'underscore';
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Drawer,
  DrawerFooter,
} from '@fluentui/react-drawer';
import { Button, makeStyles, mergeClasses } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import {
  getLoggerWithoutPageInfo,
  createImageRepository,
  createFolderRepository
} from '@bingads-webui-campaign-react/asset-library-services';
import { UiBlocker } from '@bingads-webui-fluent-component/ui-blocker';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { getLogVersion } from '@bingads-webui-campaign-react/asset-library-model';
import { addCropSettingsToCreativeImage } from '@bingads-webui-campaign-react/image-experience-model';


import { DisplayAdsEditor } from './display-ads-editor';
import { AdProps, DisplayAdsEditorDrawerProps, TemplateConfig } from './types';
import { getAdConfig } from './utils';


const useStyles = makeStyles({
  drawer: {
    width: '1270px',
    zIndex: 1000,
    top: '60px',
  },
  editorContainer: {
    height: '100%',
  },
  container: {
    marginLeft: '-24px',
    marginRight: '-24px',
    height: '100%',
  },
});

export const DisplayAdsEditorDrawer = ({
  isOpen,
  setIsOpen,
  i18n,
  perfMarker,
  permissions,
  preferenceService,
  oDataConfig,
  serverContext,
  url,
  brandKitDataService,
  brandkitApiState,
  displayTemplatesDataService,
  imageData,
  instrumentation,
  hasUpdateBrandKitData,
  hasFetchBrandKitData,
  onApplyCreative,
  scenario,
  aid,
  cid,
  sourceType,
} : DisplayAdsEditorDrawerProps) => {
  const classes = useStyles();
  const ref = React.useRef<HTMLDivElement>(null);
  const [templateDetails, setTemplateDetails] = useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [adData, setAdData] = React.useState<any>(null);
  const [saving, setSaving] = React.useState(false);
  const activity = scenario.createActivity('DisplayAdsEditorDrawer');
  const versionForLog = getLogVersion();
  const logger = getLoggerWithoutPageInfo(activity, versionForLog, 'DisplayAdsEditorDrawer');
  const folderRepository = createFolderRepository({
    i18n,
    logger,
    oDataConfig,
    aid,
    cid,
  });
  const imageRepository = createImageRepository({
    oDataConfig,
    aid,
    cid,
    logger,
    i18n,
    activity,
  });

  useEffect(() => {
    setClarityTag({ DisplayAdsEditorDrawer: 'true' });
  }, []);

  useEffect(() => {
    const parentContainer = ref.current?.parentElement;
    if (parentContainer) {
      parentContainer.style.zIndex = '1000';
    }
  }, [ref]);


  useEffect(() => {
    if (!templateDetails &&
      imageData &&
      imageData.metadata &&
      imageData.metadata.template &&
      imageData.metadata.template.templateId
    ) {
      displayTemplatesDataService
        .fetchDisplayAdsTemplateDefinition(imageData.metadata.template.templateId)
        .then(({ Data: templates }) => {
          const latestTemplate = _.max(
            templates.displayAdsTemplateDefinitions,
            (templateDefinition: any) => Number(templateDefinition.version)
          );
          setTemplateDetails({
            displayAdsTemplateDetails: [
              {
                templateConfig: latestTemplate,
                displayAd: {
                  ...imageData.metadata,
                  url: imageData.src || imageData.url,
                },
              },
            ],
          });
        })
        .catch(() => {
          // handle error
        });
    }
  }, [imageData.metadata.template.templateId, displayTemplatesDataService, templateDetails, imageData]);

  const uploadImageAsset = useCallback(({ displayAd }) => {
    if (!displayAd.image.assetId) {
      return folderRepository.addUrlImageToFolder({
        imageSrc: displayAd.image.url,
        folderId: 'ROOT',
      }).then(imageId => imageRepository.getImage(imageId));
    }
    return Promise.resolve({
      url: displayAd.image.url,
      id: displayAd.image.assetId,
    });
  }, [folderRepository, imageRepository]);

  const onSave = useCallback(() => {
    const { displayAds, adImages, templateConfigs } = adData;
    const displayAd = Object.values(displayAds)[0] as AdProps;
    const adImage = Object.values(adImages)[0] as any;
    const templateConfig = Object.values(templateConfigs)[0] as TemplateConfig;
    const displayAdConfig = getAdConfig(displayAd, templateConfig);
    let creativeData;
    setSaving(true);
    uploadImageAsset({ displayAd }).then((image) => {
      creativeData = {
        ...displayAdConfig,
        template: {
          ...displayAdConfig.template,
          image: {
            ...displayAdConfig.template.image,
            url: image.url,
          },
        },
        image: image.url,
        url: adImage.url,
      };
      imageRepository.addCreative({ creativeData, baseId: image.id, sourceType }).then((ids) => {
        if (_.isObject(_.first(ids))) {
          throw new Error(ids[0].code);
        }

        return ids[0];
      })
        .then(id => imageRepository.getImage(id))
        .then(mtObj => addCropSettingsToCreativeImage(mtObj))
        .then((imageObj) => {
          onApplyCreative({
            imageObj,
            creativeMeta: {
              placement: imageData.metadata.placement,
              ...creativeData,
            },
          });
          setIsOpen(false);
          activity.trace('DisplayAdsEditorDrawer: save');
        })
        .finally(() => {
          setSaving(false);
        });
    });
  }, [adData, onApplyCreative, setIsOpen, activity, uploadImageAsset, imageRepository, imageData, sourceType]);

  return (
    <Drawer
      type="overlay"
      modalType="modal"
      open={isOpen}
      position="end"
      size="large"
      ref={ref}
      separator
      className={mergeClasses('display-ads-editor-drawer', classes.drawer)}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={i18n.getString(_TL_('Close'))}
              icon={<Dismiss24Regular />}
              onClick={() => setIsOpen(false)}
            />
            }
        >
          {i18n.getString(_TL_('Edit your ad'))}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={classes.container}>
          <UiBlocker
            blocking={!templateDetails || saving}
            loadingText={saving? i18n.getString(_TL_('Saving...')) :i18n.getString(_TL_('Loading...'))}
          >
            { templateDetails && (
              <DisplayAdsEditor
                containerClassName={classes.editorContainer}
                i18n={i18n}
                brandKitDataService={brandKitDataService}
                brandkitApiState={brandkitApiState}
                hasFetchBrandKitData={hasFetchBrandKitData}
                hasUpdateBrandKitData={hasUpdateBrandKitData}
                perfMarker={perfMarker}
                permissions={permissions}
                oDataConfig={oDataConfig}
                serverContext={serverContext}
                instrumentation={instrumentation}
                templateData={templateDetails}
                displayTemplatesDataService={displayTemplatesDataService}
                preferenceService={preferenceService}
                url={url}
                aid={aid}
                cid={cid}
                onAdChange={setAdData}
                setLoading={setLoading}
                scenarioName="DisplayTemplateDrawer"
                activityName="DisplayTemplateDrawer"
              />)}
          </UiBlocker>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button
          appearance="primary"
          disabled={!templateDetails || loading || saving}
          onClick={onSave}
        >{i18n.getString(_TL_('Save'))}
        </Button>
        <Button
          appearance="subtle"
          disabled={saving}
          onClick={() => setIsOpen(false)}
        >
          {i18n.getString(_TL_('Cancel'))}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

